import { api } from "../../app/api";
import { EventPayload } from "../../common/types/Event";
import { Options } from "../../common/types/requestOptions";
import { SalaryType } from "../orders/types/salaryType";
import { SetEmpAttrSalaryResponse } from "../employment/employmentApi";
// todo nasty dependency to slices :/
import { EmpAttrExtraParams } from "../employment/employmentSlice";

export type TesSalaryTableStatus =
  | "DRAFT"
  | "PUBLISHING"
  | "PUBLISHED"
  | "ARCHIVED";

export type TesSalaryTableRow = {
  rowId: string;
  label: string;
  secondaryLabel?: string | null;
  sortOrder: number;
};

export type TesSalaryTableColumn = {
  columnId: string;
  label: string;
  secondaryLabel?: string | null;
  sortOrder: number;
};

export type TesSalaryTableCell = {
  rowId: string;
  columnId: string;
  hourlySalary?: string | null;
  monthlySalary?: string | null;
};

export type TesSalaryTableDocument = {
  tesSalaryTableId: string;
  version: number;
  tesId: string;
  name: string;
  effectiveDate: string;
  effectiveUntil?: string | null;
  status: TesSalaryTableStatus;
  seriesId?: string | null;
  sourceTableId?: string | null;
  successorTableId?: string | null;
  sourceTableName?: string | null;
  sourceTableEffectiveDate?: string | null;
  workTitleIds?: string[];
  createdAt?: number;
  createdBy?: string;
  createdByName?: string | null;
  updatedAt?: number;
  updatedBy?: string;
  updatedByName?: string | null;
  publishedAt?: number | null;
  publishedBy?: string | null;
  publishedByName?: string | null;
  rows: TesSalaryTableRow[];
  columns: TesSalaryTableColumn[];
  cells: TesSalaryTableCell[];
  monthlyDivider?: string | null;
};

export type TesSalaryTableSeriesDocument = {
  tesSalaryTableSeriesId: string;
  version: number;
  tesId: string;
  name: string;
  description?: string | null;
  createdAt?: number;
  createdBy?: string;
  createdByName?: string | null;
  updatedAt?: number;
  updatedBy?: string;
  updatedByName?: string | null;
};

export type TesSalaryTablesGroupedBySeriesResult = {
  series: TesSalaryTableSeriesDocument;
  tables: TesSalaryTableDocument[];
};

export type TesSalaryTableSelectionProvenance = {
  tesSalaryTableId: string;
  rowId: string;
  columnId: string;
  mode:
    | "FULL_SALARY"
    | "WITH_COMPS"
    | "INFO_ONLY";
};

export type TesSalaryTableConfig = {
  tesId: string;
  version: number;
  enabled: boolean;
};

export type ActiveTesSalaryTablesResult = {
  enabled: boolean;
  tables: TesSalaryTableDocument[];
};

export type TesSalaryTableDraftContent = {
  name: string;
  effectiveDate: string;
  monthlyDivider?: string | null;
  workTitleIds: string[];
  rows: TesSalaryTableRow[];
  columns: TesSalaryTableColumn[];
  cells: TesSalaryTableCell[];
};

export type CreateTesSalaryTableDraftParams = {
  tesSalaryTableId: string;
  version: number;
  tesId: string;
  seriesId: string;
  sourceTableId?: string | null;
  content: TesSalaryTableDraftContent;
};

export type CreateTesSalaryTableSeriesParams = {
  tesSalaryTableSeriesId: string;
  version: number;
  tesId: string;
  name: string;
  description?: string | null;
};

export type EditTesSalaryTableSeriesParams = {
  tesSalaryTableSeriesId: string;
  version: number;
  name: string;
  description?: string | null;
};

export type ReplaceTesSalaryTableDraftParams = {
  tesSalaryTableId: string;
  version: number;
  content: TesSalaryTableDraftContent;
};

export type PublishTesSalaryTableParams = {
  tesSalaryTableId: string;
  version: number;
};

export type PublishOrContinueTesSalaryTableParams = PublishTesSalaryTableParams & {
  batchSize?: number;
  includeSources?: Array<
    "EMPLOYMENT_ATTRIBUTES" | "SHIFT_EMPLOYMENT" | "EMPLOYMENT"
  >;
  cursor?: string | null;
};

export type EstimateTesSalaryTablePublishParams = {
  tesSalaryTableId: string;
};

export type TesSalaryTablePublishCandidateSelectionCount = {
  matching: number;
  nullTesSalaryTableId: number;
  alreadyTargetTesSalaryTableId: number;
  mismatchingTesSalaryTableId: number;
};

export type TesSalaryTablePublishCandidateSelectionCountByType = {
  employmentAttributes: TesSalaryTablePublishCandidateSelectionCount;
  shiftEmployments: TesSalaryTablePublishCandidateSelectionCount;
  employments: TesSalaryTablePublishCandidateSelectionCount;
};

export type EstimateTesSalaryTablePublishResult = {
  status: TesSalaryTableStatus;
  estimatedTotalCandidates: number;
  estimatedCandidates: {
    employmentAttributes: number;
    shiftEmployments: number;
    employments: number;
    total?: number;
    byType?: TesSalaryTablePublishCandidateSelectionCountByType;
  };
};

export type ValidateTesSalaryTablePublishParams = EstimateTesSalaryTablePublishParams;
export type ValidateTesSalaryTablePublishResult = EstimateTesSalaryTablePublishResult;

export type ArchiveTesSalaryTableParams = PublishTesSalaryTableParams;
export type DeleteTesSalaryTableDraftParams = PublishTesSalaryTableParams;

export type CopyTesSalaryTableToDraftParams = {
  sourceTesSalaryTableId: string;
  newTesSalaryTableId: string;
  contentOverride?: TesSalaryTableDraftContent | null;
};

export type SetTesSalaryTableConfigParams = {
  tesId: string;
  version: number;
  enabled: boolean;
};

export type TesSalaryTableCommandResult<
  TPayload extends Record<string, unknown> = Record<string, unknown>,
> = EventPayload<TPayload>[];

export type TesSalaryTablePublishCommandResult = {
  status: string;
  processedThisCall: number;
  skippedThisCall: number;
  hasMore: boolean;
  cursor?: string | null;
  eventsCreatedThisCall?: number;
  suggestedDelayMs?: number;
};

export type CountEffectiveSalariesParams = {
  tesId: string;
  date: string;
  tesSalaryTableId: string;
  sourceTypes?: string;
};

export type CountEffectiveSalariesResult = {
  count: number;
};

export type TesSalaryTableSeriesCommandPayload = {
  tesId: string;
  name: string;
  description?: string | null;
};

export type ApplyTesSalaryTableCellParams = EmpAttrExtraParams & {
  talentId: string;
  version: number;
  workTitleId: string;
  tesId: string;
  from: string;
  tesSalaryTableId: string;
  rowId: string;
  columnId: string;
  mode: "FULL_SALARY" | "WITH_COMPS" | "INFO_ONLY";
  massUpdateId?: string;
  salary?: { [key: string]: string };
  zonedSalary?: { [zone: string]: { [key: string]: string } };
};

/** TES ids with salary tables enabled and at least two published table docs; use `localized.tes` for labels. */
export const fetchTesForBulkSalaryTableIncrease = ({ requestId }: Options = {}) =>
  api
    .getWithRequestId<string[]>("/projections/qry/tesForBulkSalaryTableIncrease", {
      requestId,
      auth: true,
    })
    .then((res) => res.data);

export const fetchTesSalaryTablesByTes = (
  tesId: string,
  opts: { requestId?: string; status?: string } = {},
) =>
  api
    .getWithRequestId<TesSalaryTableDocument[]>(
      "/projections/qry/tesSalaryTablesByTes",
      {
        requestId: opts.requestId,
        queryParams: {
          tesId,
          ...(opts.status ? { status: opts.status } : {}),
        },
        auth: true,
      },
    )
    .then((res) => res.data);

export const fetchTesSalaryTablesGroupedBySeries = (
  tesId: string,
  { requestId }: Options = {},
) =>
  api
    .getWithRequestId<TesSalaryTablesGroupedBySeriesResult[]>(
      "/projections/qry/tesSalaryTablesGroupedBySeries",
      {
        requestId,
        queryParams: { tesId },
        auth: true,
      },
    )
    .then((res) => res.data);

export const fetchTesSalaryTableById = (
  tesSalaryTableId: string,
  { requestId }: Options = {},
) =>
  api
    .get<TesSalaryTableDocument | null>("/projections/qry/tesSalaryTableById", {
      requestId,
      queryParams: { tesSalaryTableId },
      auth: true,
    })
    .then((res) => res.data);

export const fetchActiveTesSalaryTables = (
  params: {
    tesId: string;
    date: string;
    workTitleId?: string;
    salaryType?: SalaryType;
  },
  { requestId }: Options = {},
) =>
  api
    .get<ActiveTesSalaryTablesResult>("/projections/qry/activeTesSalaryTables", {
      requestId,
      queryParams: {
        tesId: params.tesId,
        date: params.date,
        ...(params.workTitleId ? { workTitleId: params.workTitleId } : {}),
        ...(params.salaryType === SalaryType.Hourly ||
        params.salaryType === SalaryType.Monthly
          ? { salaryType: params.salaryType }
          : {}),
      },
      auth: true,
    })
    .then((res) => res.data);

export const fetchTesSalaryTableConfig = (
  tesId: string,
  { requestId }: Options = {},
) =>
  api
    .get<TesSalaryTableConfig>("/projections/qry/tesSalaryTableConfig", {
      requestId,
      queryParams: { tesId },
      auth: true,
    })
    .then((res) => res.data);

export const createTesSalaryTableDraft = (
  params: CreateTesSalaryTableDraftParams,
  { requestId }: Options = {},
) =>
  api.post<TesSalaryTableCommandResult>("/working/cmd/createTesSalaryTableDraft", {
    requestId,
    auth: true,
    body: params,
  }).then((res) => res.data);

export const createTesSalaryTableSeries = (
  params: CreateTesSalaryTableSeriesParams,
  { requestId }: Options = {},
) =>
  api.postWithRequestId<TesSalaryTableCommandResult<TesSalaryTableSeriesCommandPayload>>(
    "/working/cmd/createTesSalaryTableSeries",
    {
    requestId,
    auth: true,
    body: params,
    },
  ).then((res) => res.data);

export const editTesSalaryTableSeries = (
  params: EditTesSalaryTableSeriesParams,
  { requestId }: Options = {},
) =>
  api.postWithRequestId<TesSalaryTableCommandResult<TesSalaryTableSeriesCommandPayload>>(
    "/working/cmd/editTesSalaryTableSeries",
    {
    requestId,
    auth: true,
    body: params,
    },
  ).then((res) => res.data);

export const replaceTesSalaryTableDraft = (
  params: ReplaceTesSalaryTableDraftParams,
  { requestId }: Options = {},
) =>
  api.post<TesSalaryTableCommandResult>("/working/cmd/replaceTesSalaryTableDraft", {
    requestId,
    auth: true,
    body: params,
  }).then((res) => res.data);

export const publishTesSalaryTable = (
  params: PublishTesSalaryTableParams,
  { requestId }: Options = {},
) =>
  api.post<TesSalaryTableCommandResult>("/working/cmd/publishTesSalaryTable", {
    requestId,
    auth: true,
    body: params,
  }).then((res) => res.data);

export const publishOrContinueTesSalaryTable = (
  params: PublishOrContinueTesSalaryTableParams,
  { requestId }: Options = {},
) =>
  api.post<TesSalaryTablePublishCommandResult>("/working/cmd/publishOrContinueTesSalaryTable", {
    requestId,
    auth: true,
    body: params,
  }).then((res) => res.data);

export const estimateTesSalaryTablePublish = (
  params: EstimateTesSalaryTablePublishParams,
  { requestId }: Options = {},
) =>
  api
    .getWithRequestId<EstimateTesSalaryTablePublishResult>(
      "/projections/qry/estimateTesSalaryTablePublish",
      {
        requestId,
        queryParams: params,
        auth: true,
      },
    )
    .then((res) => res.data);

export const validateTesSalaryTablePublish = estimateTesSalaryTablePublish;

export const countEffectiveSalaries = (
  params: CountEffectiveSalariesParams,
  { requestId }: Options = {},
) =>
  api
    .getWithRequestId<CountEffectiveSalariesResult>(
      "/projections/qry/effectiveSalariesSearch",
      {
        requestId,
        queryParams: { ...params, countOnly: "true" },
        auth: true,
      },
    )
    .then((res) => res.data);

export const archiveTesSalaryTable = (
  params: ArchiveTesSalaryTableParams,
  { requestId }: Options = {},
) =>
  api.post<TesSalaryTableCommandResult>("/working/cmd/archiveTesSalaryTable", {
    requestId,
    auth: true,
    body: params,
  }).then((res) => res.data);

export const deleteTesSalaryTableDraft = (
  params: DeleteTesSalaryTableDraftParams,
  { requestId }: Options = {},
) =>
  api.post<TesSalaryTableCommandResult>("/working/cmd/deleteTesSalaryTableDraft", {
    requestId,
    auth: true,
    body: params,
  }).then((res) => res.data);

export const copyTesSalaryTableToDraft = (
  params: CopyTesSalaryTableToDraftParams,
  { requestId }: Options = {},
) =>
  api.post<TesSalaryTableCommandResult>("/working/cmd/copyTesSalaryTableToDraft", {
    requestId,
    auth: true,
    body: params,
  }).then((res) => res.data);

export const setTesSalaryTableConfig = (
  params: SetTesSalaryTableConfigParams,
  { requestId }: Options = {},
) =>
  api.post<TesSalaryTableCommandResult>("/working/cmd/setTesSalaryTableConfig", {
    requestId,
    auth: true,
    body: params,
  }).then((res) => res.data);

export const applyTesSalaryTableCellToEmploymentAttributes = (
  params: ApplyTesSalaryTableCellParams,
  { requestId }: Options = {},
) =>
  // we are calling this from employmentSlice and we actually have same response
  api.post<SetEmpAttrSalaryResponse[]>("/working/cmd/applyTesSalaryTableCellToEmpAttr", {
    requestId,
    auth: true,
    body: params,
  }).then((res) => res.data[0]);
